import { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Avatar,
    Button,
    Card,
    Input,
    List,
    Modal,
    Select,
    Typography,
    Space,
    Row,
    Col,
    Divider,
    Badge,
    Tag,
    Tooltip,
    InputNumber,
} from "antd";
import {
    EditOutlined,
    ExclamationCircleOutlined,
    DeleteOutlined,
    PlusOutlined,
    SaveOutlined,
    UserOutlined,
    InfoCircleOutlined,
    TeamOutlined,
    FileExcelOutlined,
} from "@ant-design/icons";
import axiosInstance from "../../../../../variables/api/axiosInstance";
import { toast } from "react-toastify";
import AddVictime from "../modals/AddVictime";
import AddAyantDroit from "../modals/AddAyantDroit";
import DetailsVictime from "../modals/DetailsVictime";
import VictimesExcelImport from "../modals/VictimesExcelImport";

const { Title, Text, Paragraph } = Typography;

// Refonte UI V2 (variant="v2") : présentation moderne, identité navy PoolIns, avatars locaux.
const initialsOf = (nom, prenom) => {
    const a = (nom || "").trim(), b = (prenom || "").trim();
    return ((a[0] || "") + (b[0] || "")).toUpperCase() || "V";
};
const VICT_V2_CSS = `
.vict-v2{--navy:#242166;--ink:#181A2C;--muted:#636A85;--faint:#9BA0B8;--line:#E7E8F2;--line-2:#EFF0F7;
  --ok:#0E9F6E;--warn:#C77A08;--crit:#DC2626;--navy-tint:#EDECF8;
  --shadow:0 1px 2px rgba(24,26,44,.04),0 6px 20px -10px rgba(24,26,44,.14)}
.vict-v2 .vc{background:#fff;border:1px solid var(--line);border-radius:14px;box-shadow:var(--shadow);
  overflow:hidden;cursor:pointer;transition:border-color .12s,box-shadow .12s;height:100%}
.vict-v2 .vc:hover{border-color:#CFD2E6;box-shadow:0 10px 28px -12px rgba(24,26,44,.28)}
.vict-v2 .vc-top{display:flex;align-items:flex-start;gap:12px;padding:16px 16px 12px}
.vict-v2 .vc-av{width:46px;height:46px;border-radius:12px;flex-shrink:0;display:grid;place-items:center;
  font-weight:750;font-size:16px;background:var(--navy-tint);color:var(--navy)}
.vict-v2 .vc-av.deces{background:#FBE7E7;color:var(--crit)}
.vict-v2 .vc-id{flex:1;min-width:0}
.vict-v2 .vc-nom{font-size:15px;font-weight:700;color:var(--ink);letter-spacing:-.01em;line-height:1.2}
.vict-v2 .vc-tags{display:flex;gap:6px;flex-wrap:wrap;margin-top:7px}
.vict-v2 .vp{display:inline-flex;align-items:center;gap:6px;font-size:11.5px;font-weight:650;
  padding:3px 10px;border-radius:999px;line-height:1;white-space:nowrap}
.vict-v2 .vp .d{width:6px;height:6px;border-radius:50%;background:currentColor}
.vict-v2 .vp.warn{color:#9A5E06;background:#FBF0DC}
.vict-v2 .vp.crit{color:#B91C1C;background:#FBE7E7}
.vict-v2 .vp.info{color:#1D4FD8;background:#E6EDFD}
.vict-v2 .vp.navy{color:var(--navy);background:var(--navy-tint)}
.vict-v2 .vc-body{padding:0 16px 14px;display:flex;flex-direction:column;gap:9px;border-top:1px solid var(--line-2);padding-top:12px}
.vict-v2 .vc-kv{display:flex;flex-direction:column;gap:1px}
.vict-v2 .vc-kv .l{font-size:11px;font-weight:600;letter-spacing:.03em;text-transform:uppercase;color:var(--faint)}
.vict-v2 .vc-kv .v{font-size:13px;color:var(--ink);overflow:hidden;text-overflow:ellipsis}
.vict-v2 .vc-dom{font-size:12.5px;color:#4b5563;background:#FAFAFE;border:1px solid var(--line-2);
  border-radius:9px;padding:8px 10px;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
.vict-v2 .vc-foot{padding:0 12px 12px}
.vict-v2 .vc-add{width:100%;border:1px dashed #C7CAE6;background:#FCFCFE;color:var(--navy);
  border-radius:10px;padding:9px;font-size:13px;font-weight:600;cursor:pointer}
.vict-v2 .vc-add:hover{background:var(--navy-tint)}
`;

const VictimesTab = ({ sinistre, victimes, setRefetch, editMode, setEditMode, variant }) => {
    const isV2 = variant === "v2";
    const readOnly = !!sinistre?.lectureSeule;
    const [victimesList, setVictimesList] = useState(victimes || []);
    const [nombreBlessesLegers, setNombreBlessesLegers] = useState(sinistre?.nombreBlesseLeger);
    const [nombreBlessesMoyens, setNombreBlessesMoyens] = useState(sinistre?.nombreBlesseMoyen);
    const [nombreBlessesGraves, setNombreBlessesGraves] = useState(sinistre?.nombreBlesseGrave);
    const [nombreDeces, setNombreDeces] = useState(Number(sinistre?.nombreDeces) || 0);
    // Nombre de victimes déclaré (sinistre MATÉRIEL : on précise combien de victimes ajouter).
    const [nombreVictimes, setNombreVictimes] = useState(Number(sinistre?.nombreVictimes) || 0);
    const [typeDegats, setTypeDegats] = useState(sinistre?.typeDegats);
    const [showVictimeUpdateAlert, setShowVictimeUpdateAlert] = useState(false);
    const [loading, setLoading] = useState(false);
    const [isVictimeModalVisible, setIsVictimeModalVisible] = useState(false);
    const [isAyantDroitModalVisible, setIsAyantDroitModalVisible] = useState(false);
    const [isDetailsVictimeModalVisible, setIsDetailsVictimeModalVisible] = useState(false);
    const [isImportVictimesModalVisible, setIsImportVictimesModalVisible] = useState(false);
    const [currentVictime, setCurrentVictime] = useState(null);
    const [motifVictimeUpdate, setMotifVictimeUpdate] = useState("");
    const [isVictimeUpdateModalVisible, setIsVictimeUpdateModalVisible] = useState(false);
    const [deleteLoadingId, setDeleteLoadingId] = useState(null);
    const [adversaireOptions, setAdversaireOptions] = useState([]);

    useEffect(() => {
        setNombreBlessesLegers(sinistre?.nombreBlesseLeger);
        setNombreBlessesMoyens(sinistre?.nombreBlesseMoyen);
        setNombreBlessesGraves(sinistre?.nombreBlesseGrave);
        setNombreDeces(Number(sinistre?.nombreDeces) || 0);
        setNombreVictimes(Number(sinistre?.nombreVictimes) || 0);
        setTypeDegats(sinistre?.typeDegats);
        setAdversaireOptions(sinistre?.victimes?.filter((v) => v.adversaire) || []);
    }, [sinistre]);

    useEffect(() => {
        setVictimesList(victimes || []);
    }, [victimes]);

    const victimesSansAdversaire = useMemo(
        () => victimesList?.filter((victime) => !victime.adversaire || victime.adversaire === null) || [],
        [victimesList]
    );

    // Victimes AFFICHÉES dans cette section = victimes propres du dossier + parties adverses
    // ayant subi des dommages CORPORELS ou MIXTES (leurs blessés sont des victimes à
    // indemniser). Une partie adverse purement MATÉRIELLE (ou « Aucun ») n'apparaît PAS ici.
    const victimesAffichees = useMemo(
        () => (victimesList || []).filter(
            (v) => !v.adversaire || v.adversaire === null
                || v.typeSinistre === "MIXTE" || v.typeSinistre === "CORPOREL"
        ),
        [victimesList]
    );

    const estMateriel = sinistre?.typeSinistre === "MATERIEL";
    // Sinistre MATÉRIEL : c'est le nombre de victimes ENREGISTRÉ (sinistre.nombreVictimes) qui
    // fait foi — l'utilisateur doit d'abord PRÉCISER puis ENREGISTRER ce nombre (« Mettre à jour »)
    // avant de pouvoir ajouter des victimes. Sinon (corporel/mixte) : somme blessés + décès.
    const nombreVictimesEnregistre = Number(sinistre?.nombreVictimes || 0);
    const totalVictimesDeclarees = estMateriel
        ? nombreVictimesEnregistre
        : (
            Number(nombreBlessesLegers || 0) +
            Number(nombreBlessesMoyens || 0) +
            Number(nombreBlessesGraves || 0) +
            Number(nombreDeces || 0)
        );

    const maxVictimesAtteint = totalVictimesDeclarees > 0 && victimesSansAdversaire.length >= totalVictimesDeclarees;
    // MATÉRIEL : tant que le nombre de victimes n'est pas déclaré (et enregistré), on interdit l'ajout.
    const doitDeclarerVictimes = estMateriel && nombreVictimesEnregistre <= 0;

    const handleNewVictimeOpen = () => {
        if (readOnly) { toast.warn("Dossier en lecture seule : action impossible."); return; }
        if (doitDeclarerVictimes) {
            Modal.warning({
                title: "Nombre de victimes requis",
                content: "Veuillez d'abord préciser le nombre de victimes puis cliquer sur « Mettre à jour » avant d'en ajouter.",
            });
            return;
        }
        if (maxVictimesAtteint) {
            Modal.warning({
                title: "Limite atteinte",
                content: "Le nombre de victimes ajoutées ne peut pas dépasser le nombre déclaré.",
            });
            return;
        }
        setCurrentVictime(null);
        setEditMode(false);
        setIsVictimeModalVisible(true);
    };

    const handleNewAyantDroitOpen = (victime) => {
        if (readOnly) { toast.warn("Dossier en lecture seule : action impossible."); return; }
        setCurrentVictime(victime);
        setIsAyantDroitModalVisible(true);
    };

    const editVictime = (victime) => {
        if (readOnly) { toast.warn("Dossier en lecture seule : action impossible."); return; }
        setCurrentVictime(victime);
        setEditMode(true);
        setIsVictimeModalVisible(true);
    };

    const handleVictimesCountUpdate = (motif) => {
        if (readOnly) { toast.warn("Dossier en lecture seule : action impossible."); return; }
        setLoading(true);
        // Simulation de mise à jour
        const {
            nombreBlesseMoyen,
            nombreBlesseGrave,
            nombreBlesseLeger,
            nombreDeces: nombreDecesInitial,
            nombreVictimes: nombreVictimesInitial,
            typeDegats: typeDegatsInitial,
        } = sinistre || {};

        if (
            nombreBlesseMoyen === Number(nombreBlessesMoyens) &&
            nombreBlesseGrave === Number(nombreBlessesGraves) &&
            nombreBlesseLeger === Number(nombreBlessesLegers) &&
            Number(nombreDecesInitial || 0) === Number(nombreDeces || 0) &&
            Number(nombreVictimesInitial || 0) === Number(nombreVictimes || 0) &&
            typeDegatsInitial === typeDegats
        ) {
            toast("Aucun changement detecté!");
            setLoading(false);
            return;
        }

        const payload = {
            ...sinistre,
            nombreBlesseMoyen: Number(nombreBlessesMoyens),
            nombreBlesseGrave: Number(nombreBlessesGraves),
            nombreBlesseLeger: Number(nombreBlessesLegers),
            nombreDeces: Number(nombreDeces),
            nombreVictimes: Number(nombreVictimes),
            typeDegats: typeDegats || sinistre?.typeDegats,
        };

        if (motif) {
            payload.motifModification = motif;
        }

        axiosInstance
            .put(`/api/sinistre/victimes-count`, JSON.stringify(payload))
            .then((res) => {
                const data = res.data;
                setNombreBlessesMoyens(data.nombreBlesseMoyen);
                setNombreBlessesGraves(data.nombreBlesseGrave);
                setNombreBlessesLegers(data.nombreBlesseLeger);
                setNombreDeces(data.nombreDeces);
                setNombreVictimes(Number(data.nombreVictimes) || 0);
                setTypeDegats(data.typeDegats);
                toast("Nombre de victimes mis à jour!");
                setRefetch && setRefetch((prev) => !prev);
                setShowVictimeUpdateAlert(false);
                setIsVictimeUpdateModalVisible(false);
            })
            .catch(() => {
                toast.error("Impossible de mettre à jour le nombre de victimes");
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleVictimeUpdateModalOk = () => {
        handleVictimesCountUpdate(motifVictimeUpdate);
    };

    const getSeverityColor = (type) => {
        const colors = {
            leger: "#52c41a",
            moyen: "#faad14",
            grave: "#ff4d4f",
            deces: "#000000",
        };
        return colors[type] || "#d9d9d9";
    };

    const isDossierClos = sinistre?.sortDossier === "S" || sinistre?.sortDossier === "T";

    const removeVictimeFromList = (victimeId) => {
        setVictimesList((previous) =>
            (previous || []).filter(
                (victime) => (victime?.codeId || victime?.id) !== victimeId
            )
        );
    };

    const deleteVictime = (victimeId) => {
        if (readOnly) { toast.warn("Dossier en lecture seule : action impossible."); return; }
        setDeleteLoadingId(victimeId);
        return axiosInstance
            .delete(`/api/victime/${victimeId}`)
            .then(() => {
                toast.success("Victime supprimée avec succès");
                removeVictimeFromList(victimeId);
                if (currentVictime && (currentVictime.codeId === victimeId || currentVictime.id === victimeId)) {
                    setCurrentVictime(null);
                    setIsDetailsVictimeModalVisible(false);
                }
                setRefetch && setRefetch((prev) => !prev);
            })
            .catch(() => {
                toast.error("Impossible de supprimer la victime");
            })
            .finally(() => {
                setDeleteLoadingId(null);
            });
    };

    const confirmDeleteVictime = (victime) => {
        const victimeId = victime?.codeId || victime?.id;
        if (!victimeId) {
            toast.error("Identifiant de la victime introuvable");
            return;
        }

        Modal.confirm({
            title: "Supprimer cette victime ?",
            icon: <ExclamationCircleOutlined />,
            content: "Cette action supprimera définitivement la victime du dossier.",
            okText: "Supprimer",
            cancelText: "Annuler",
            okButtonProps: {
                danger: true,
                style: { backgroundColor: "#ff4d4f", borderColor: "#ff4d4f" },
            },
            onOk: () => deleteVictime(victimeId),
        });
    };

    return (
        <div
            className={isV2 ? "vict-v2" : ""}
            style={isV2 ? { padding: 0 } : { padding: "24px", background: "#f5f7fa", minHeight: "100vh" }}
        >
            {isV2 && <style>{VICT_V2_CSS}</style>}
            <div style={isV2 ? {} : { maxWidth: "1400px", margin: "0 auto" }}>
                {/* En-tête (masqué en V2 : le cockpit fournit déjà le contexte du dossier) */}
                {!isV2 && (
                <div style={{
                    background: '#242166',
                    padding: "16px 32px",
                    borderRadius: "12px",
                    marginBottom: "24px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)"
                }}>
                    <Title level={2} style={{ color: "white", margin: 0 }}>
                        <UserOutlined style={{ marginRight: "12px" }} />
                        Gestion des Victimes
                    </Title>
                    <Text style={{ color: "rgba(255,255,255,0.9)", fontSize: "16px" }}>
                        Sinistre N°: {sinistre?.numSinistre || sinistre?.numeroSinistreM || "N/A"} - Type: {sinistre?.typeSinistre || "N/A"}
                    </Text>
                </div>
                )}

                {/* Formulaire de déclaration */}
                <Card
                    title={
                        <Space>
                            <InfoCircleOutlined />
                            <span>Déclaration des victimes</span>
                        </Space>
                    }
                    style={{ marginBottom: "20px", borderRadius: "8px" }}
                >
                    {sinistre?.typeSinistre === "MATERIEL" ? (
                        <Row gutter={16}>
                            <Col xs={24} sm={12}>
                                <div style={{ marginBottom: "8px" }}>
                                    <Text strong>Type de dégâts</Text>
                                </div>
                                <Select
                                    value={typeDegats}
                                    onChange={setTypeDegats}
                                    style={{ width: "100%" }}
                                    size="large"
                                    placeholder="Sélectionner le type de dégâts"
                                    options={[
                                        { value: "Dégats légers ou sans précision", label: "Dégats légers ou sans précision" },
                                        { value: "Dégâts moyens", label: "Dégâts moyens" },
                                        { value: "Dégâts importants", label: "Dégâts importants" },
                                        { value: "Cas extrêmes", label: "Cas extrêmes" },
                                    ]}
                                />
                            </Col>
                            <Col xs={24} sm={12}>
                                <div style={{ marginBottom: "8px" }}>
                                    <Text strong>Nombre de victimes</Text>
                                </div>
                                <InputNumber
                                    size="large"
                                    min={0}
                                    value={nombreVictimes}
                                    onChange={(e) => setNombreVictimes(Number(e) || 0)}
                                    placeholder="0"
                                    style={{ width: "100%" }}
                                />
                            </Col>
                        </Row>
                    ) : (
                        <Row gutter={[16, 16]}>
                            <Col xs={24} sm={12} lg={6}>
                                <div style={{ marginBottom: "8px" }}>
                                    <Text strong>Blessés légers</Text>
                                </div>
                                <InputNumber
                                    size="large"
                                    value={nombreBlessesLegers}
                                    onChange={(e) => setNombreBlessesLegers(e)}
                                    placeholder="0"
                                    style={{ width: "100%" }}
                                    min={0}
                                />
                            </Col>
                            <Col xs={24} sm={12} lg={6}>
                                <div style={{ marginBottom: "8px" }}>
                                    <Text strong>Blessés moyens</Text>
                                </div>
                                <InputNumber
                                    size="large"
                                    value={nombreBlessesMoyens}
                                    onChange={(e) => setNombreBlessesMoyens(e)}
                                    placeholder="0"
                                    style={{ width: "100%" }}
                                    min={0}
                                />
                            </Col>
                            <Col xs={24} sm={12} lg={6}>
                                <div style={{ marginBottom: "8px" }}>
                                    <Text strong>Blessés graves</Text>
                                </div>
                                <InputNumber
                                    size="large"
                                    value={nombreBlessesGraves}
                                    onChange={(e) => setNombreBlessesGraves(e)}
                                    placeholder="0"
                                    style={{ width: "100%" }}
                                    min={0}
                                />
                            </Col>
                            <Col xs={24} sm={12} lg={6}>
                                <div style={{ marginBottom: "8px" }}>
                                    <Text strong>Décès</Text>
                                </div>
                                <InputNumber
                                    size="large"
                                    value={nombreDeces}
                                    onChange={(e) => setNombreDeces(Number(e))}
                                    placeholder="0"
                                    style={{ width: "100%" }}
                                    min={0}
                                />
                            </Col>
                            {sinistre?.typeSinistre === "MIXTE" && (
                                <Col xs={24} sm={12} lg={6}>
                                    <div style={{ marginBottom: "8px" }}>
                                        <Text strong>Type de dégâts</Text>
                                    </div>
                                    <Select
                                        value={typeDegats}
                                        onChange={setTypeDegats}
                                        style={{ width: "100%" }}
                                        size="large"
                                        placeholder="Type de dégâts"
                                        options={[
                                            { value: "Dégats légers ou sans précision", label: "Dégats légers" },
                                            { value: "Dégâts moyens", label: "Dégâts moyens" },
                                            { value: "Dégâts importants", label: "Dégâts importants" },
                                            { value: "Cas extrêmes", label: "Cas extrêmes" },
                                        ]}
                                    />
                                </Col>
                            )}
                        </Row>
                    )}

                    <Divider />

                    {showVictimeUpdateAlert && (
                        <Alert
                            type="warning"
                            showIcon
                            message="Mise à jour requise"
                            description="Vous venez d'ajouter une victime, veuillez mettre à jour les champs correspondants."
                            style={{ marginBottom: 16 }}
                            closable
                            onClose={() => setShowVictimeUpdateAlert(false)}
                        />
                    )}

                    <Space>
                        <Button
                            type="primary"
                            style={{ background: '#242166' }}
                            icon={<SaveOutlined />}
                            loading={loading}
                            size="large"
                            disabled={isDossierClos || readOnly}
                            onClick={() => setIsVictimeUpdateModalVisible(true)}
                        >
                            Mettre à jour
                        </Button>
                        {isDossierClos && (
                            <Tag color="red">Dossier clôturé</Tag>
                        )}
                    </Space>
                </Card>

                {/* Liste des victimes */}
                <Card
                    title={
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Space>
                                <UserOutlined />
                                <span>Victimes enregistrées</span>
                                <Badge count={victimesAffichees?.length || 0} showZero style={{ backgroundColor: "#52c41a" }} />
                            </Space>
                            <Space>
                                <Button
                                    icon={<FileExcelOutlined />}
                                    size="large"
                                    onClick={() => setIsImportVictimesModalVisible(true)}
                                    disabled={isDossierClos || doitDeclarerVictimes || readOnly}
                                >
                                    Importer (Excel)
                                </Button>
                                <Button
                                    type="primary"
                                    style={{ background: '#242166' }}
                                    icon={<PlusOutlined />}
                                    size="large"
                                    onClick={handleNewVictimeOpen}
                                    disabled={isDossierClos || maxVictimesAtteint || doitDeclarerVictimes || readOnly}
                                >
                                    Nouvelle victime
                                </Button>
                            </Space>
                        </div>
                    }
                    style={{ borderRadius: "8px" }}
                >
                    {doitDeclarerVictimes && (
                        <Alert
                            type="warning"
                            showIcon
                            message="Nombre de victimes à préciser"
                            description="Sinistre matériel : précisez le nombre de victimes ci-dessus, puis cliquez sur « Mettre à jour » avant de pouvoir ajouter des victimes."
                            style={{ marginBottom: 16 }}
                        />
                    )}
                    {maxVictimesAtteint && (
                        <Alert
                            type="info"
                            showIcon
                            message="Limite atteinte"
                            description={`Vous avez atteint le nombre maximum de victimes (${totalVictimesDeclarees}). Veuillez augmenter le nombre déclaré pour en ajouter davantage.`}
                            style={{ marginBottom: 16 }}
                        />
                    )}

                    {victimesAffichees?.length > 0 ? (
                        <List
                            grid={{
                                gutter: 16,
                                xs: 1,
                                sm: 1,
                                md: 2,
                                lg: 2,
                                xl: 2,
                                xxl: 3,
                            }}
                            dataSource={victimesAffichees}
                            renderItem={(item, index) => (
                                isV2 ? (
                                <List.Item>
                                    <div className="vc" onClick={() => { setCurrentVictime(item); setIsDetailsVictimeModalVisible(true); }}>
                                        <div className="vc-top">
                                            <span className={`vc-av ${item?.decesSinistre ? "deces" : ""}`}>{initialsOf(item?.personne?.nom, item?.personne?.prenom)}</span>
                                            <div className="vc-id">
                                                <div className="vc-nom">{`${item?.personne?.nom || ""} ${item?.personne?.prenom || ""}`.trim() || "Victime"}</div>
                                                <div className="vc-tags">
                                                    {item?.decesSinistre
                                                        ? <span className="vp crit"><span className="d"></span>Décédé(e)</span>
                                                        : item?.blesseSinistre
                                                            ? <span className="vp warn"><span className="d"></span>Blessé(e)</span>
                                                            : <span className="vp info"><span className="d"></span>Autre</span>}
                                                    {item?.representant && <span className="vp navy"><TeamOutlined style={{ fontSize: 11 }} /> Représentant</span>}
                                                    {(item?.ayantsDroit?.length || 0) > 0 && <span className="vp info">{item.ayantsDroit.length} ayant(s) droit</span>}
                                                </div>
                                            </div>
                                            <div style={{ display: "flex", gap: 2 }} onClick={(e) => e.stopPropagation()}>
                                                <Tooltip title="Modifier"><Button type="text" size="small" icon={<EditOutlined />} disabled={readOnly} onClick={() => editVictime(item)} /></Tooltip>
                                                <Tooltip title="Supprimer"><Button type="text" size="small" danger icon={<DeleteOutlined />}
                                                    loading={deleteLoadingId === (item?.codeId || item?.id)}
                                                    disabled={readOnly}
                                                    onClick={() => confirmDeleteVictime(item)} /></Tooltip>
                                            </div>
                                        </div>
                                        <div className="vc-body">
                                            <div className="vc-kv"><span className="l">Profession</span><span className="v">{item?.personne?.profession || "Non renseignée"}</span></div>
                                            <div className="vc-kv"><span className="l">Adresse</span><span className="v">{item?.personne?.adresse || "Non renseignée"}</span></div>
                                            {item?.descriptionDegatsVictime && <div className="vc-dom">{item.descriptionDegatsVictime}</div>}
                                        </div>
                                        {item?.decesSinistre && (
                                            <div className="vc-foot" onClick={(e) => e.stopPropagation()}>
                                                <button className="vc-add" disabled={readOnly} onClick={() => handleNewAyantDroitOpen(item)}>+ Ajouter un ayant-droit</button>
                                            </div>
                                        )}
                                    </div>
                                </List.Item>
                                ) : (
                                <List.Item>
                                    <Card
                                        hoverable
                                        style={{
                                            borderRadius: "8px",
                                            border: "1px solid #e8e8e8",
                                            transition: "all 0.3s",
                                            backgroundColor: "ghostwhite",
                                        }}
                                        styles={{
                                            body: { padding: "20px" }
                                        }}
                                        onClick={() => {
                                            setCurrentVictime(item);
                                            setIsDetailsVictimeModalVisible(true);
                                        }}
                                    >
                                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "16px" }}>
                                            <Space>
                                                <Avatar
                                                    size={48}
                                                    src={`https://ui-avatars.com/api/?name=${item?.personne?.prenom
                                                        ? `${item?.personne?.nom}+${item?.personne?.prenom}`
                                                        : item?.personne?.nom || "V"
                                                        }&background=667eea&color=fff`}
                                                />
                                                <div>
                                                    <Text strong style={{ fontSize: "16px" }}>
                                                        {item?.personne?.prenom
                                                            ? `${item?.personne?.nom} ${item?.personne?.prenom}`
                                                            : item?.personne?.nom || "Victime"}
                                                    </Text>
                                                    <br />
                                                    <Tag color={item?.blesseSinistre ? "orange" : "red"}>
                                                        {item?.blesseSinistre ? "Blessé(e)" : item?.decesSinistre ? "Décédé(e)" : ""}
                                                    </Tag>
                                                    {item?.representant && (
                                                        <Tooltip title="Représentant de la victime">
                                                            <Tag color="geekblue" icon={<TeamOutlined />}>
                                                                Représentant : {`${item.representant.nom || ""} ${item.representant.prenom || ""}`.trim() || "renseigné"}
                                                            </Tag>
                                                        </Tooltip>
                                                    )}
                                                </div>
                                            </Space>
                                            <Tooltip title="Modifier">
                                                <Button
                                                    type="text"
                                                    icon={<EditOutlined />}
                                                    disabled={readOnly}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        editVictime(item);
                                                    }}
                                                />
                                            </Tooltip>
                                            <Tooltip title="Supprimer">
                                                <Button
                                                    type="text"
                                                    danger
                                                    icon={<DeleteOutlined />}
                                                    loading={deleteLoadingId === (item?.codeId || item?.id)}
                                                    disabled={deleteLoadingId === (item?.codeId || item?.id) || readOnly}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        confirmDeleteVictime(item);
                                                    }}
                                                />
                                            </Tooltip>
                                        </div>

                                        <Divider style={{ margin: "12px 0" }} />

                                        <Space direction="vertical" style={{ width: "100%" }}>
                                            <div>
                                                <Text type="secondary">Profession</Text>
                                                <br />
                                                <Text>{item?.personne?.profession || "Non renseignée"}</Text>
                                            </div>
                                            <div>
                                                <Text type="secondary">Adresse</Text>
                                                <br />
                                                <Text ellipsis>{item?.personne?.adresse || "Non renseignée"}</Text>
                                            </div>
                                            {item?.descriptionDegatsVictime && (
                                                <div>
                                                    <Text type="secondary">Dommages</Text>
                                                    <br />
                                                    <Paragraph
                                                        ellipsis={{ rows: 2, expandable: false }}
                                                        style={{ margin: 0 }}
                                                    >
                                                        {item?.descriptionDegatsVictime}
                                                    </Paragraph>
                                                </div>
                                            )}
                                        </Space>

                                        {item?.decesSinistre && (
                                            <>
                                                <Divider style={{ margin: "12px 0" }} />
                                                <Button
                                                    type="dashed"
                                                    block
                                                    icon={<PlusOutlined />}
                                                    disabled={readOnly}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleNewAyantDroitOpen(item);
                                                    }}
                                                >
                                                    Ajouter un ayant-droit
                                                </Button>
                                            </>
                                        )}
                                    </Card>
                                </List.Item>
                            ))}
                        />
                    ) : (
                        <div style={{
                            textAlign: "center",
                            padding: "60px 0",
                            color: "#999"
                        }}>
                            <UserOutlined style={{ fontSize: "64px", marginBottom: "16px" }} />
                            <br />
                            <Text style={{ fontSize: "16px" }}>
                                Aucune victime enregistrée sur ce dossier sinistre
                            </Text>
                        </div>
                    )}
                </Card>

                {/* Modal de confirmation */}
                <Modal
                    title="Confirmation de mise à jour"
                    open={isVictimeUpdateModalVisible}
                    onOk={handleVictimeUpdateModalOk}
                    onCancel={() => {
                        setIsVictimeUpdateModalVisible(false);
                        setMotifVictimeUpdate("");
                    }}
                    okText="Confirmer"
                    cancelText="Annuler"
                    okButtonProps={{ disabled: !motifVictimeUpdate.trim(), style: { "backgroundColor": "#242166" } }}
                >
                    <Space direction="vertical" style={{ width: "100%" }}>
                        <Text>Voulez-vous vraiment effectuer cette mise à jour ?</Text>
                        <Input.TextArea
                            rows={4}
                            placeholder="Veuillez indiquer le motif de cette modification..."
                            value={motifVictimeUpdate}
                            onChange={(e) => setMotifVictimeUpdate(e.target.value)}
                            style={{ marginTop: "12px" }}
                        />
                    </Space>
                </Modal>
                <AddVictime
                    open={isVictimeModalVisible}
                    modalExpert={isVictimeModalVisible}
                    victime={currentVictime}
                    setOpen={setIsVictimeModalVisible}
                    sinistre={sinistre}
                    edit={editMode}
                    onSaved={({ action }) => {
                        setRefetch && setRefetch((previous) => !previous);
                        setEditMode(false);
                        if (action === "create") {
                            setShowVictimeUpdateAlert(true);
                        }
                    }}
                    adversaires={adversaireOptions || []}
                />

                <VictimesExcelImport
                    open={isImportVictimesModalVisible}
                    setOpen={setIsImportVictimesModalVisible}
                    sinistre={sinistre}
                    setRefetch={setRefetch}
                />

                <AddAyantDroit
                    open={isAyantDroitModalVisible}
                    modalExpert={isAyantDroitModalVisible}
                    victime={currentVictime}
                    setOpen={setIsAyantDroitModalVisible}
                    ayantDroit={null}
                    edit={editMode}
                />

                <DetailsVictime
                    open={isDetailsVictimeModalVisible}
                    victime={currentVictime}
                    setVictime={setCurrentVictime}
                    setOpen={setIsDetailsVictimeModalVisible}
                />
            </div>
        </div>
    );
};

export default VictimesTab;
